import { FetchBaseQueryError, FetchBaseQueryMeta, QueryReturnValue } from '@reduxjs/toolkit/query';
import { getBoardId } from '@/core/web-storage/webStorage.utils';
import { baseAppApi } from '@/core/network/baseAppApi';
import { COMMON_HEADERS } from '@/core/network/network.constants';
import {
  EmployeeByIdContributions,
  EmployeeByIdErrorType,
  EmployeeType,
  EmployerType,
  LatestBatchStatusType,
} from '@/common/common.types';
import {
  RECORDS_PER_PAGE,
  saveFilterResultToStorage,
} from '@/payroll-period/utils/payrollPeriod.utils';
import {
  AppStandardResponseWithoutNesting,
  getFirstErrorOrDefault,
  getRangeFromHeader,
} from '@/core/network/network.utils';
import tokens from '@/payroll-period/tokens.json';
import { addBoardInfo, resetBoardInfo } from '@/core/store/commonReducer';

interface EmployeesByPeriodRequest {
  period: string;
  pageNumber?: number;
  pageSize?: number;
  errorType?: string;
  employerId?: number; // applicable only when filters are applied
  filters?: Record<string, string>; // applicable only when filters are applied
}

export interface EmployeesByPeriodEmployeeType {
  employeeId: number;
  lastName: string;
  firstName: string;
  sin: string;
  employerEmployeeNumber: string;
  preSubmitWarning: number;
  preSubmitError: number;
  preSubmitCritical: number;
  contributionError: number;
  contributionCount: number;
  latestBatchStatus: LatestBatchStatusType;
  totalEmployeeContributions: number;
  totalAtaFees: number;
}

export type EmployeesByPeriodResponse = {
  range: [number, number, number];
  employees: EmployeesByPeriodEmployeeType[];
};

interface EmployeeDetailsByIdRequest {
  employeeId: number;
}

export interface EmployeeDetailsByIdResponse extends EmployeeType {
  errors: EmployeeByIdErrorType[];
  contributions: EmployeeByIdContributions[];
}

export type EmployerDetailsByIdResponse = EmployerType;
type EmployerDetailsByIdRequest = {
  employerId: number;
};

export type UpdateEmployerRequestPayloadType = {
  city: string;
  contactName: string;
  contactPhone: string;
  contactEmailAddress: string;
  defaultAtaLocalNumber: string;
  employerName: string;
  employerPayoutType: string;
  faxNumber: string;
  mailingCity: string;
  mailingPostalCode: string;
  mailingProvince: string;
  mailingStreetAddress: string;
  pensionPlan: string;
  phoneNumber: string;
  postalCode: string;
  province: string;
  streetAddress: string;
  streetAddressLine2: string;
  mailingStreetAddressLine2: string;
  mailingFaxNumber: string;
  mailingPhoneNumber: string;
  mailingEmail: string;
};

export type UpdateEmployerRequestType = {
  employerId: number;
  requestBody: UpdateEmployerRequestPayloadType;
};

export type ApproveBypassRequestType = {
  employerId: number;
  note: string;
};

export type CopyBatchPayloadExludingSourceEmployerId = Omit<
  CopyBatchPayloadType,
  'sourceEmployerId'
>;
export type CopyBatchPayloadType = {
  sourceEmployerId: number;
  fromEmploymentMonth: string;
  toEmploymentMonth: string;
};

const payrollPeriodApi = baseAppApi.injectEndpoints({
  endpoints: (build) => ({
    getEmployeesByPeriod: build.mutation<EmployeesByPeriodResponse, EmployeesByPeriodRequest>({
      async queryFn(
        { period, errorType, employerId, filters, pageNumber = 1, pageSize = RECORDS_PER_PAGE },
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) {
        const boardId = getBoardId();
        const selectedErrorType = errorType === '-1' ? '' : errorType;
        let employeeIdsFromSP: number[] | undefined = undefined;
        let metaForRange: FetchBaseQueryMeta | undefined = undefined;

        if (employerId && filters) {
          const response = (await fetchWithBQ({
            method: 'POST',
            url: `/v1/employers/${boardId}/employees/${employerId}/searchbyperiod`,
            params: { pageNumber, pageSize },
            headers: COMMON_HEADERS,
            body: {
              ...filters,
            },
          })) as QueryReturnValue<AppStandardResponseWithoutNesting<{ employeeId: number }[]>>;

          if (response.error) {
            return {
              error: response.error as FetchBaseQueryError,
            };
          }

          employeeIdsFromSP = response.data?.data.map((response) => response.employeeId);

          if (employeeIdsFromSP && Array.isArray(employeeIdsFromSP)) {
            if (employeeIdsFromSP.length === 0) {
              return {
                data: {
                  employees: [],
                  range: [0, 0, 0],
                },
              };
            } else {
              metaForRange = response.meta as FetchBaseQueryMeta;
            }
          }
        }

        const employees = (await fetchWithBQ({
          url: `v1/employers/${boardId}/employees/periods/${period}`,
          params: {
            pageNum: employeeIdsFromSP && Array.isArray(employeeIdsFromSP) ? 1 : pageNumber,
            pageSize,
            errorType: selectedErrorType,
          },
          method: 'POST',
          headers: COMMON_HEADERS,
          body: employeeIdsFromSP ?? [],
        })) as QueryReturnValue<
          AppStandardResponseWithoutNesting<{
            employeeHeaders: EmployeesByPeriodEmployeeType[];
          }>
        >;

        if (employees.error) {
          return {
            error: employees.error as FetchBaseQueryError,
          };
        }

        if (employees.data && employees.data.data) {
          const { employeeHeaders } = employees.data.data;
          if (employeeHeaders) {
            if (employeeHeaders.length === 0) {
              return {
                data: {
                  employees: [],
                  range: [0, 0, 0],
                },
              };
            } else {
              let range = [0, 0, 0] as [number, number, number];
              if (Array.isArray(employeeIdsFromSP)) {
                range = getRangeFromHeader(metaForRange as FetchBaseQueryMeta);
              } else {
                range = getRangeFromHeader(employees.meta as FetchBaseQueryMeta);
              }

              saveFilterResultToStorage(
                employees.data?.data.employeeHeaders.map((employee) => employee.employeeId),
              );
              return {
                data: {
                  employees: employees.data?.data.employeeHeaders,
                  range,
                },
              };
            }
          }
        }

        return {
          error: { status: 'CUSTOM_ERROR', error: tokens['ERROR_FETCHING'] },
        };
      },
    }),
    getEmployeeDetails: build.query<EmployeeDetailsByIdResponse, EmployeeDetailsByIdRequest>({
      query({ employeeId }) {
        const boardId = getBoardId();
        return {
          method: 'GET',
          url: `/v1/employers/${boardId}/employees/${employeeId}`,
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(response: AppStandardResponseWithoutNesting<EmployeeDetailsByIdResponse>) {
        return {
          ...response.data,
          contributions: response.data.contributions.map((contribution) => ({
            ...contribution,
            PreSubmitErrors:
              contribution.preSubmitErrors === null
                ? []
                : contribution.preSubmitErrors?.filter((error) => error.description !== ''),
          })),
        };
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['ERROR_FETCHING']);
      },
      providesTags: ['EMPLOYEE'],
    }),
    getEmployerDetails: build.query<EmployerDetailsByIdResponse, EmployerDetailsByIdRequest>({
      query({ employerId }) {
        const boardId = getBoardId();
        return {
          method: 'GET',
          url: `/v1/employers/${boardId}/${employerId}`,
          headers: COMMON_HEADERS,
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const {
            data: { boardNum, employerName },
          } = await queryFulfilled;
          dispatch(
            addBoardInfo({
              name: employerName,
              number: boardNum,
            }),
          );
        } catch (e) {
          dispatch(resetBoardInfo());
        }
      },
      transformResponse(response: { data: EmployerDetailsByIdResponse }) {
        return response.data;
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['EMPLOYER_DETAILS_ERROR']);
      },
      providesTags: ['EMPLOYER'],
    }),
    updateEmployer: build.mutation<void, UpdateEmployerRequestType>({
      query({ employerId, requestBody }) {
        const boardId = getBoardId();
        return {
          method: 'POST',
          url: `/v1/employers/${boardId}/${employerId}`,
          body: requestBody,
          headers: COMMON_HEADERS,
        };
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['EMPLOYER_SAVE_ERROR']);
      },
      invalidatesTags: (_, error) => (error ? [] : ['EMPLOYER']),
    }),
    approveBypass: build.mutation<void, ApproveBypassRequestType>({
      query({ employerId, note }) {
        const boardId = getBoardId();
        return {
          method: 'POST',
          url: `/v1/employers/${boardId}/approveBypass`,
          body: { employerId, note },
          headers: COMMON_HEADERS,
        };
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['APPROVE_BYPASS_ERROR']);
      },
    }),
    resetBatch: build.mutation<void, { employerId: number }>({
      query({ employerId }) {
        const boardId = getBoardId();
        return {
          method: 'POST',
          url: `/v1/employers/${boardId}/batches/${employerId}/reset`,
          headers: COMMON_HEADERS,
        };
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['RESET_BATCH_BANNER_ERROR']);
      },
      invalidatesTags: ['EMPLOYEE'],
    }),
    copyForwardBatch: build.mutation<void, CopyBatchPayloadType>({
      query(body) {
        const boardId = getBoardId();
        return {
          method: 'POST',
          url: `/v1/employers/${boardId}/copybatch`,
          headers: COMMON_HEADERS,
          body,
        };
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['COPY_FORWARD_BANNER_ERROR']);
      },
    }),
  }),
});

export { payrollPeriodApi };
